import { supabase } from '../supabaseClient';
import type { RosterRuleViolation } from './dutyBoardService';

export type ShiftSegmentType = 'duty' | 'break';
export type ShiftWelfareMode = 'inherit' | 'required' | 'disabled';

interface ShiftProfileRecord {
  full_name?: string | null;
  avatar_url?: string | null;
}

interface ShiftSiteRecord {
  name?: string | null;
  timezone?: string | null;
  country_code?: string | null;
}

interface ShiftSegmentRecord {
  id: string;
  company_id: string;
  shift_id: string;
  site_id: string;
  guard_id: string;
  slot_date: string;
  segment_order: number;
  source_display_order: number;
  segment_type: ShiftSegmentType;
  source_assignment_id: string;
  source_slot_id: string;
  start_minute: number;
  end_minute: number;
  label: string;
  duty_kind?: string | null;
  colour?: string | null;
  is_paid?: boolean | null;
  is_published: boolean;
  notes?: string | null;
  patrol_id?: string | null;
  post_id?: string | null;
}

interface ShiftRecord {
  id: string;
  shift_key: string;
  company_id: string;
  site_id: string;
  guard_id: string;
  slot_date: string;
  start_minute: number;
  end_minute: number;
  source_segment_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  guard?: ShiftProfileRecord | null;
  site?: ShiftSiteRecord | null;
  segments?: ShiftSegmentRecord[] | null;
}

export interface ShiftSegment {
  id: string;
  company_id: string;
  shift_id: string;
  site_id: string;
  guard_id: string;
  slot_date: string;
  segment_order: number;
  source_display_order: number;
  segment_type: ShiftSegmentType;
  source_assignment_id: string;
  source_slot_id: string;
  start_minute: number;
  end_minute: number;
  label: string;
  duty_kind: string | null;
  colour: string | null;
  is_paid: boolean | null;
  is_published: boolean;
  notes: string | null;
  patrol_id: string | null;
  post_id: string | null;
}

export interface Shift {
  id: string;
  shift_key: string;
  company_id: string;
  site_id: string;
  guard_id: string;
  slot_date: string;
  start_minute: number;
  end_minute: number;
  source_segment_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  guard_name: string;
  guard_avatar_url: string | null;
  site_name: string;
  site_timezone: string | null;
  site_country_code: string | null;
  segments: ShiftSegment[];
  rule_violations: RosterRuleViolation[];
  welfare_check_mode: ShiftWelfareMode;
}

const SHIFT_SELECT = `
  id,
  shift_key,
  company_id,
  site_id,
  guard_id,
  slot_date,
  start_minute,
  end_minute,
  source_segment_count,
  is_published,
  created_at,
  updated_at,
  guard:profiles(full_name, avatar_url),
  site:sites(name, timezone, country_code),
  segments:shift_segments(
    id,
    company_id,
    shift_id,
    site_id,
    guard_id,
    slot_date,
    segment_order,
    source_display_order,
    segment_type,
    source_assignment_id,
    source_slot_id,
    start_minute,
    end_minute,
    label,
    duty_kind,
    colour,
    is_paid,
    is_published,
    notes,
    patrol_id,
    post_id
  )
`;

function mapShiftRows(rows: ShiftRecord[]): Shift[] {
  return rows.map(toShift);
}

function toShiftSegment(row: ShiftSegmentRecord): ShiftSegment {
  return {
    id: row.id,
    company_id: row.company_id,
    shift_id: row.shift_id,
    site_id: row.site_id,
    guard_id: row.guard_id,
    slot_date: row.slot_date,
    segment_order: row.segment_order,
    source_display_order: row.source_display_order,
    segment_type: row.segment_type,
    source_assignment_id: row.source_assignment_id,
    source_slot_id: row.source_slot_id,
    start_minute: row.start_minute,
    end_minute: row.end_minute,
    label: row.label,
    duty_kind: row.duty_kind ?? null,
    colour: row.colour ?? null,
    is_paid: row.is_paid ?? null,
    is_published: row.is_published,
    notes: row.notes ?? null,
    patrol_id: row.patrol_id ?? null,
    post_id: row.post_id ?? null,
  };
}

function toShift(row: ShiftRecord): Shift {
  const segments = ((row.segments ?? []) as ShiftSegmentRecord[])
    .map(toShiftSegment)
    .sort((left, right) => left.segment_order - right.segment_order);

  return {
    id: row.id,
    shift_key: row.shift_key,
    company_id: row.company_id,
    site_id: row.site_id,
    guard_id: row.guard_id,
    slot_date: row.slot_date,
    start_minute: row.start_minute,
    end_minute: row.end_minute,
    source_segment_count: row.source_segment_count,
    is_published: row.is_published,
    created_at: row.created_at,
    updated_at: row.updated_at,
    guard_name: row.guard?.full_name?.trim() || 'Unknown guard',
    guard_avatar_url: row.guard?.avatar_url ?? null,
    site_name: row.site?.name?.trim() || 'Unknown site',
    site_timezone: row.site?.timezone ?? null,
    site_country_code: row.site?.country_code ?? null,
    segments,
    rule_violations: [],
    welfare_check_mode: 'inherit',
  };
}

interface ShiftWelfareSettingRecord {
  company_id: string;
  site_id: string;
  slot_date: string;
  shift_key: string;
  mode: Exclude<ShiftWelfareMode, 'inherit'>;
}

const shiftWelfareIdentity = (
  value: Pick<Shift, 'company_id' | 'site_id' | 'slot_date' | 'shift_key'>,
): string => `${value.company_id}:${value.site_id}:${value.slot_date}:${value.shift_key}`;

async function attachWelfareSettings(shifts: Shift[]): Promise<Shift[]> {
  if (shifts.length === 0) return shifts;

  const siteIds = [...new Set(shifts.map((shift) => shift.site_id))];
  const shiftKeys = [...new Set(shifts.map((shift) => shift.shift_key))];
  const dates = shifts.map((shift) => shift.slot_date).sort();
  const { data, error } = await supabase
    .from('shift_welfare_settings')
    .select('company_id, site_id, slot_date, shift_key, mode')
    .in('site_id', siteIds)
    .in('shift_key', shiftKeys)
    .gte('slot_date', dates[0])
    .lte('slot_date', dates[dates.length - 1]);

  if (error) throw error;

  const settings = new Map(
    ((data ?? []) as ShiftWelfareSettingRecord[]).map((setting) => [
      shiftWelfareIdentity(setting),
      setting.mode,
    ]),
  );

  return shifts.map((shift) => ({
    ...shift,
    welfare_check_mode: settings.get(shiftWelfareIdentity(shift)) ?? 'inherit',
  }));
}

function toRosterRuleViolation(row: Record<string, unknown>): RosterRuleViolation {
  return {
    id: (row.id as string | null) ?? null,
    slot_date: row.slot_date as string,
    country_code: ((row.country_code as string | null) ?? '').trim(),
    roster_rule_id: row.roster_rule_id as string,
    rule_code: row.rule_code as string,
    rule_name: row.rule_name as string,
    severity: row.severity === 'warning' ? 'warning' : 'blocker',
    guard_id: (row.guard_id as string | null) ?? null,
    guard_name: (row.guard_name as string | null) ?? null,
    shift_id: (row.shift_id as string | null) ?? null,
    source_slot_ids: Array.isArray(row.source_slot_ids) ? row.source_slot_ids as string[] : [],
    message: row.message as string,
    details: (row.details && typeof row.details === 'object' ? row.details : {}) as Record<string, unknown>,
    is_overridden: Boolean(row.is_overridden),
    overridden_by: (row.overridden_by as string | null) ?? null,
    overridden_at: (row.overridden_at as string | null) ?? null,
    created_at: (row.created_at as string | null) ?? null,
  };
}

const ROSTER_RULE_VIOLATION_SELECT = `
  id,
  slot_date,
  country_code,
  roster_rule_id,
  rule_code,
  rule_name,
  severity,
  guard_id,
  guard_name,
  shift_id,
  source_slot_ids,
  message,
  details,
  is_overridden,
  overridden_by,
  overridden_at,
  created_at
`;

async function fetchSiteRosterRuleViolations(
  siteId: string,
  startDate: string,
  endDate: string,
): Promise<RosterRuleViolation[]> {
  const { data, error } = await supabase
    .from('roster_rule_violations')
    .select(ROSTER_RULE_VIOLATION_SELECT)
    .eq('site_id', siteId)
    .eq('is_current', true)
    .gte('slot_date', startDate)
    .lte('slot_date', endDate);

  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(toRosterRuleViolation);
}

async function fetchGuardRosterRuleViolations(
  guardId: string,
  startDate: string,
  endDate: string,
): Promise<RosterRuleViolation[]> {
  const { data, error } = await supabase
    .from('roster_rule_violations')
    .select(ROSTER_RULE_VIOLATION_SELECT)
    .eq('guard_id', guardId)
    .eq('is_current', true)
    .gte('slot_date', startDate)
    .lte('slot_date', endDate);

  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(toRosterRuleViolation);
}

function attachRosterRuleViolations(
  shifts: Shift[],
  violations: RosterRuleViolation[],
): Shift[] {
  return shifts.map((shift) => {
    const sourceSlotIds = new Set(shift.segments.map((segment) => segment.source_slot_id));
    const ruleViolations = violations.filter((violation) => {
      if (violation.shift_id && violation.shift_id === shift.id) return true;
      if (violation.guard_id !== shift.guard_id || violation.slot_date !== shift.slot_date) return false;
      if (violation.source_slot_ids.length === 0) return true;
      return violation.source_slot_ids.some((slotId) => sourceSlotIds.has(slotId));
    });

    return {
      ...shift,
      rule_violations: ruleViolations,
    };
  });
}

export const shiftService = {
  async fetchSiteShifts(siteId: string, slotDate: string): Promise<Shift[]> {
    const { data, error } = await supabase
      .from('shifts')
      .select(SHIFT_SELECT)
      .eq('site_id', siteId)
      .eq('slot_date', slotDate)
      .order('start_minute', { ascending: true })
      .order('guard_id', { ascending: true });

    if (error) throw error;

    const shifts = await attachWelfareSettings(mapShiftRows((data ?? []) as ShiftRecord[]));
    const violations = await fetchSiteRosterRuleViolations(siteId, slotDate, slotDate);
    return attachRosterRuleViolations(shifts, violations);
  },

  async fetchSiteShiftsRange(
    siteId: string,
    startDate: string,
    endDate: string,
  ): Promise<Record<string, Shift[]>> {
    const { data, error } = await supabase
      .from('shifts')
      .select(SHIFT_SELECT)
      .eq('site_id', siteId)
      .gte('slot_date', startDate)
      .lte('slot_date', endDate)
      .order('slot_date', { ascending: true })
      .order('start_minute', { ascending: true })
      .order('guard_id', { ascending: true });

    if (error) throw error;

    const shifts = await attachWelfareSettings(mapShiftRows((data ?? []) as ShiftRecord[]));
    const violations = await fetchSiteRosterRuleViolations(siteId, startDate, endDate);
    const shiftsWithViolations = attachRosterRuleViolations(shifts, violations);
    const grouped: Record<string, Shift[]> = {};

    shiftsWithViolations.forEach((shift) => {
      if (!grouped[shift.slot_date]) grouped[shift.slot_date] = [];
      grouped[shift.slot_date].push(shift);
    });

    return grouped;
  },

  async fetchGuardShiftsRange(
    guardId: string,
    startDate: string,
    endDate: string,
  ): Promise<Shift[]> {
    const { data, error } = await supabase
      .from('shifts')
      .select(SHIFT_SELECT)
      .eq('guard_id', guardId)
      .gte('slot_date', startDate)
      .lte('slot_date', endDate)
      .order('slot_date', { ascending: true })
      .order('start_minute', { ascending: true })
      .order('site_id', { ascending: true });

    if (error) throw error;

    const shifts = await attachWelfareSettings(mapShiftRows((data ?? []) as ShiftRecord[]));
    const violations = await fetchGuardRosterRuleViolations(guardId, startDate, endDate);
    return attachRosterRuleViolations(shifts, violations);
  },

  async setShiftWelfareMode(
    shiftId: string,
    mode: ShiftWelfareMode,
  ): Promise<void> {
    const { error } = await supabase.rpc('set_shift_welfare_mode', {
      p_shift_id: shiftId,
      p_mode: mode,
    });

    if (error) throw error;
  },
};
